// components/teams/TeamDetailsPanel.tsx
import React from 'react';
import { Team, TeamRequest, TeamApplication } from '../../types/team';
import { formatDistanceToNow } from 'date-fns';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/components/teams/TeamDetailsPanel.css';

interface TeamDetailsPanelProps {
  team?: Team | null;
  teamRequest?: TeamRequest | null;
  application?: TeamApplication | null;
  onClose: () => void;
  type: 'team' | 'request' | 'application';
  // Additional props for actions
  onFormTeam?: (teamRequestId: string) => void;
  onManageTeam?: (team: Team) => void;
  acceptedAppsCount?: number;
  isFormingTeam?: boolean;
}

const TeamDetailsPanel: React.FC<TeamDetailsPanelProps> = ({
  team,
  teamRequest,
  application,
  onClose,
  type,
  onFormTeam,
  onManageTeam,
  acceptedAppsCount = 0,
  isFormingTeam = false
}) => {
  const { t } = useLanguage();
  const renderTeamDetails = () => {
    if (!team) return null;

    return (
      <div className="team-details-container">
        <div className="team-details-header">
          <div className="team-details-header-main">
            <div className="team-details-title-area">
              <h1 className="team-details-title">{team.name}</h1>
              <div className="team-details-metadata">
                <span className={`team-status-badge status-${team.status}`}>
                  <i className="bi bi-circle-fill"></i>
                  {team.status.toUpperCase()}
                </span>
                <span className="team-details-date-badge">
                  <i className="bi bi-calendar3"></i>
                  {t('teams.createdAgo', { time: formatDistanceToNow(new Date(team.created_at)) })}
                </span>
                <span className="team-details-members-badge">
                  <i className="bi bi-people-fill"></i>
                  {team.members_count || 0} {t('teams.members')}
                </span>
              </div>
            </div>
            <div className="team-details-header-actions">
              <button
                className="task-details-action-btn new-tab"
                onClick={() => window.open(`/setup-team?teamId=${team.id}`, '_blank')}
                title={t('teams.openInNewTab')}
              >
                <i className="bi bi-box-arrow-up-right"></i>
              </button>
              <button
                className="task-details-action-btn close"
                onClick={onClose}
                aria-label={t('teams.closeTeamDetails')}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="team-details-body">
          <div className="team-details-section">
            <h3>{t('teams.aboutThisTeam')}</h3>
            <p>{team.description || team.project_details || t('teams.noDescriptionAvailable')}</p>
          </div>

          <div className="team-details-section">
            <h3>{t('teams.teamMembers')}</h3>
            <div className="team-members-grid">
              {team.members && team.members.length > 0 ? (
                team.members.map((member: any, index: number) => (
                  <div key={member.id || index} className="team-member-card">
                    <div className="member-avatar">
                      {member.user?.avatar_url ? (
                        <img src={member.user.avatar_url} alt={member.user.display_name || member.user.username} />
                      ) : (
                        <div className="avatar-placeholder">
                          {(member.user?.display_name || member.user?.username || 'U').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="member-info">
                      <div className="member-name">
                        {member.user?.display_name || member.user?.username || t('teams.unknownUser')}
                      </div>
                      <div className="member-role">
                        <span className={`role-badge role-${member.role}`}>
                          {member.role === 'owner' && <i className="bi bi-star-fill"></i>}
                          {member.role === 'member' && <i className="bi bi-person-fill"></i>}
                          {member.role}
                        </span>
                      </div>
                      <div className="member-joined">
                        {t('teams.joinedAgo', { time: formatDistanceToNow(new Date(member.joined_at)) })}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-members">
                  <i className="bi bi-people"></i>
                  <p>{t('teams.noMemberInfo')}</p>
                </div>
              )}
            </div>
          </div>

          {team.owner && (
            <div className="team-details-section">
              <h3>{t('teams.teamOwner')}</h3>
              <div className="team-owner-info">
                <div className="owner-avatar">
                  {team.owner.avatar_url ? (
                    <img src={team.owner.avatar_url} alt={team.owner.display_name || team.owner.username} />
                  ) : (
                    <div className="avatar-placeholder">
                      {(team.owner.display_name || team.owner.username || 'O').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="owner-details">
                  <div className="owner-name">{team.owner.display_name || team.owner.username}</div>
                  <div className="owner-role">{t('teams.teamCreatorLeader')}</div>
                </div>
              </div>
            </div>
          )}

          {onManageTeam && (
            <div className="team-details-section">
              <h3>{t('teams.teamManagement')}</h3>
              <div className="team-action-section">
                <button
                  className="btn btn-primary btn-manage-team"
                  onClick={() => onManageTeam(team)}
                >
                  <i className="bi bi-gear"></i>
                  {t('teams.manageTeam')}
                </button>
                <p className="action-help-text">
                  {t('teams.manageTeamHelp')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRequestDetails = () => {
    if (!teamRequest) return null;

    return (
      <div className="team-details-container">
        <div className="team-details-header">
          <div className="team-details-header-main">
            <div className="team-details-title-area">
              <h1 className="team-details-title">{teamRequest.title}</h1>
              <div className="team-details-metadata">
                <span className={`team-status-badge status-${teamRequest.status}`}>
                  <i className="bi bi-circle-fill"></i>
                  {teamRequest.status.toUpperCase()}
                </span>
                <span className="team-details-date-badge">
                  <i className="bi bi-calendar3"></i>
                  {t('teams.createdAgo', { time: formatDistanceToNow(new Date(teamRequest.created_at)) })}
                </span>
                <span className="team-details-members-badge">
                  <i className="bi bi-people-fill"></i>
                  {t('teams.teamSize')}: {teamRequest.team_size}
                </span>
                <span className="team-details-cost-badge">
                  <i className="bi bi-coin"></i>
                  {teamRequest.points_cost} {t('teams.points')}
                </span>
              </div>
            </div>
            <div className="team-details-header-actions">
              <button
                className="task-details-action-btn new-tab"
                onClick={() => window.open(`/setup-team?requestId=${teamRequest.id}`, '_blank')}
                title={t('teams.openInNewTab')}
              >
                <i className="bi bi-box-arrow-up-right"></i>
              </button>
              <button
                className="task-details-action-btn close"
                onClick={onClose}
                aria-label={t('teams.closeRequestDetails')}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="team-details-body">
          <div className="team-details-section">
            <h3>{t('teams.projectDescription')}</h3>
            <p>{teamRequest.project_introduction}</p>
          </div>

          <div className="team-details-section">
            <h3>{t('teams.requirements')}</h3>
            <div className="requirement-items">
              <div className="requirement-item">
                <i className="bi bi-clock"></i>
                <span>{t('teams.timeCommitment')}: {teamRequest.min_time_commitment}</span>
              </div>
              {teamRequest.deadline && (
                <div className="requirement-item">
                  <i className="bi bi-calendar-check"></i>
                  <span>{t('teams.deadline')}: {new Date(teamRequest.deadline).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {teamRequest.skills_required && teamRequest.skills_required.length > 0 && (
            <div className="team-details-section">
              <h3>{t('teams.requiredSkills')}</h3>
              <div className="skills-grid">
                {teamRequest.skills_required.map((skill, index) => (
                  <span key={index} className="skill-tag">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="team-details-section">
            <h3>{t('teams.teamApplications')}</h3>
            <div className="applications-summary">
              <span className="applications-count">
                {t('teams.applicationsReceived', { count: teamRequest.applications_count || 0 })}
              </span>
            </div>
            {acceptedAppsCount >= (teamRequest.team_size - 1) && onFormTeam && (
              <div className="team-action-section">
                <button
                  className="btn btn-success btn-form-team"
                  onClick={() => onFormTeam(teamRequest.id)}
                  disabled={isFormingTeam}
                >
                  <i className="bi bi-people"></i>
                  {isFormingTeam ? t('teams.formingTeam') : t('teams.formTeamButton', { count: acceptedAppsCount })}
                </button>
                <p className="action-help-text">
                  {t('teams.formTeamHelp', { count: acceptedAppsCount, plural: acceptedAppsCount !== 1 ? 's' : '' })}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderApplicationDetails = () => {
    if (!application) return null;

    return (
      <div className="team-details-container">
        <div className="team-details-header">
          <div className="team-details-header-main">
            <div className="team-details-title-area">
              <h1 className="team-details-title">{t('teams.applicationDetails')}</h1>
              <div className="team-details-metadata">
                <span className="team-details-category-badge">
                  <i className="bi bi-person-fill"></i>
                  {application.applicant?.display_name || application.applicant?.username}
                </span>
                <span className={`application-status-badge status-${application.status}`}>
                  {application.status.toUpperCase()}
                </span>
                <span className="team-details-date-badge">
                  <i className="bi bi-calendar3"></i>
                  {t('teams.appliedLabel')} {application.applied_at ? new Date(application.applied_at).toLocaleDateString() : t('teams.dateNotAvailable')}
                </span>
                {application.reviewed_at && (
                  <span className="team-details-date-badge">
                    <i className="bi bi-check-circle"></i>
                    {t('teams.reviewedLabel')} {new Date(application.reviewed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <div className="team-details-header-actions">
              <button
                className="task-details-action-btn new-tab"
                onClick={() => window.open(`/setup-team?applicationId=${application.id}`, '_blank')}
                title={t('teams.openInNewTab')}
              >
                <i className="bi bi-box-arrow-up-right"></i>
              </button>
              <button
                className="task-details-action-btn close"
                onClick={onClose}
                aria-label={t('teams.closeApplicationDetails')}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="team-details-body">
          <div className="team-details-section">
            <h3>{t('teams.forTeamRequest')} {application.team_request?.title}</h3>
            <p>{application.team_request?.project_introduction}</p>
          </div>

          <div className="team-details-section">
            <h3>{t('teams.introduction')}</h3>
            <p>{application.introduction}</p>
          </div>

          <div className="team-details-section">
            <h3>{t('teams.whyImGoodFit')}</h3>
            <p>{application.why_fit}</p>
          </div>

          {application.skills_highlight && application.skills_highlight.length > 0 && (
            <div className="team-details-section">
              <h3>{t('teams.skillsHighlight')}</h3>
              <div className="skills-grid">
                {application.skills_highlight.map((skill, index) => (
                  <span key={index} className="skill-tag highlighted">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {application.availability && (
            <div className="team-details-section">
              <h3>{t('teams.availability')}</h3>
              <p>{application.availability}</p>
            </div>
          )}

          {application.publisher_message && (
            <div className="team-details-section">
              <h3>{t('teams.publisherResponse')}</h3>
              <div className="publisher-message">
                <p>{application.publisher_message}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!team && !teamRequest && !application) {
    return (
      <div className="team-details-empty">
        <div className="team-details-empty-content">
          <div className="team-details-empty-icon">
            {type === 'team' && <i className="bi bi-people"></i>}
            {type === 'request' && <i className="bi bi-clipboard-check"></i>}
            {type === 'application' && <i className="bi bi-file-text"></i>}
          </div>
          <h3 className="team-details-empty-title">
            {type === 'team' && t('teams.selectATeam')}
            {type === 'request' && t('teams.selectATeamRequest')}
            {type === 'application' && t('teams.selectAnApplication')}
          </h3>
          <p className="team-details-empty-description">
            {type === 'team' && t('teams.selectTeamDescription')}
            {type === 'request' && t('teams.selectRequestDescription')}
            {type === 'application' && t('teams.selectApplicationDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-details-panel">
      {type === 'team' && renderTeamDetails()}
      {type === 'request' && renderRequestDetails()}
      {type === 'application' && renderApplicationDetails()}
    </div>
  );
};

export default TeamDetailsPanel;
